import { BrowserModule } from '@angular/platform-browser';
import { CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, LOCALE_ID, NgModule, NgZone } from '@angular/core';
import {
  APP_BASE_HREF,
  HashLocationStrategy,
  LocationStrategy,
  NgOptimizedImage,
} from '@angular/common';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgRedux, NgReduxModule } from '@angular-redux3/store';
import { RouterModule } from '@angular/router';
import { LazyElementsModule } from '@angular-extensions/elements';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { NxtSpinnerOverlayModule } from '@nxt/ngx-ndbx-extensions/spinner-overlay';
import { NxCopytextModule } from '@allianz/ng-aquila/copytext';
import { TEXT_SERVICE_CONTENT, TextServiceModule } from '@ps20-frontend/text-service';
import { GlobalErrorHandler } from '@ps20-frontend/logging';
import { App } from '@ps20-frontend/frontend-models';
import { NxExpertModule } from '@allianz/ng-aquila/config';
import {
  GlobalActions,
  GlobalEventDispatcher,
  GlobalEventDispatcherService,
} from '@ps20-frontend/shop-integration-redux';
import { LinkModule, ModalModule } from '@ps20-frontend/gui-components';
import { NxIconModule } from '@allianz/ng-aquila/icon';
import { AllianzIconModule } from '@allianz/ngx-brand-kit/icon';
import { NxButtonModule } from '@allianz/ng-aquila/button';
import { NxIsoDateModule } from '@allianz/ng-aquila/iso-date-adapter';
import { NxCheckboxModule } from '@allianz/ng-aquila/checkbox';
import { FormsModule } from '@angular/forms';
import { NxtFieldSetModule } from '@nxt/ngx-ndbx-extensions/fieldset';
import { NxFormfieldModule } from '@allianz/ng-aquila/formfield';
import { AuthConfig, OAuthModule, OAuthService } from '@angular-redux3/angular-oauth2-oidc';
import { initDayJs } from '@ps20-frontend/utils';
import { NxColComponent, NxLayoutComponent, NxRowComponent } from '@allianz/ng-aquila/grid';
import { NxHeadlineComponent } from '@allianz/ng-aquila/headline';
import { Role } from '@ps20/bff-models';
import { IAppState, localStore, reducers } from '../store/localStore';
import { init, initGlobalEventDispatcher } from '../portal';
import contentDefault from '../data/content-default.json';
import contentVertreter from '../data/content-vertreter.json';
import contentAzam from '../data/content-azam.json';
import contentSdw from '../data/content-sdw.json';
import { UrlParameterLoader } from '../urlParameterLoader';
import { environment } from '../environments/environment';
import { rootReducer } from '../store/root.reducer';
import { DashboardComponent } from './dashboard/dashboard.component';
import { VhvAngebotComponent } from './vhv-angebot/vhv-angebot.component';
import { CheckoutComponent } from './checkout/checkout.component';
import { PhAngebotComponent } from './ph-angebot/ph-angebot.component';
import { RsAngebotComponent } from './rs-angebot/rs-angebot.component';
import { VwgAngebotComponent } from './vwg-angebot/vwg-angebot.component';
import { ThhHundAngebotComponent } from './thh-hund-angebot/thh-hund-angebot.component';
import { ThhPferdAngebotComponent } from './thh-pferd-angebot/thh-pferd-angebot.component';
import { DesignContainerComponent } from './design-container/design-container.component';
import { ExpertModeDesignComponent } from './design-container/expert-mode-design/expert-mode-design.component';
import { NdbxDesignComponent } from './design-container/ndbx-design/ndbx-design.component';
import { ErrorpageComponent } from './errorpage/errorpage.component';
import { SessionExpiredPageComponent } from './session-expired-page/session-expired-page.component';
import { ConstructionpageComponent } from './constructionpage/constructionpage.component';
import { TkvAngebotComponent } from './tkv-angebot/tkv-angebot.component';
import { AppComponent } from './app.component';
import { LogoutpageComponent } from './logoutpage/logoutpage.component';
import { UnfAngebotComponent } from './unf-angebot/unf-angebot.component';
import { JhpAngebotComponent } from './jhp-angebot/jhp-angebot.component';
import { GegeAngebotComponent } from './gege-angebot/gege-angebot.component';
import { VnAddressInvalidComponent } from './vn-address-invalid/vn-address-invalid.component';
import { RsVermietungAngebotComponent } from './rs-vermietung-angebot/rs-vermietung-angebot.component';
import { TkvAngebotV2Component } from './tkv-angebot-v2/tkv-angebot-v2.component';
import { VwgAngebotV2Component } from './vwg-angebot-v2/vwg-angebot-v2.component';
import { VhvAngebotV2Component } from './vhv-angebot-v2/vhv-angebot-v2.component';
import { TgvAngebotComponent } from './tgv-angebot/tgv-angebot.component';
import { ForwardedToUnderwriterPageComponent } from './forwarded-to-underwriter-page/forwarded-to-underwriter-page.component';
import { DevMockPanelComponent } from './dev-mock/dev-mock-panel.component';

@NgModule({
  imports: [
    AppComponent,
    BrowserModule,
    BrowserAnimationsModule,
    LazyElementsModule,
    NgReduxModule,
    NxtSpinnerOverlayModule,
    NxCopytextModule,
    TextServiceModule,
    NxExpertModule,
    NxIconModule,
    RouterModule.forRoot(
      [
        { path: 'dashboard', component: DashboardComponent },
        { path: 'vwg/angebot/:subroute', component: VwgAngebotComponent },
        { path: 'vwg-v2/angebot/:subroute', component: VwgAngebotV2Component },
        { path: 'vhv/angebot/:subroute', component: VhvAngebotComponent },
        { path: 'vhv-v2/angebot/:subroute', component: VhvAngebotV2Component },
        { path: 'ph/angebot/:subroute', component: PhAngebotComponent },
        { path: 'rs/angebot/:subroute', component: RsAngebotComponent },
        { path: 'rs-vermietung/angebot/:subroute', component: RsVermietungAngebotComponent },
        { path: 'unf/angebot/:subroute', component: UnfAngebotComponent },
        { path: 'jhp/angebot/:subroute', component: JhpAngebotComponent },
        { path: 'gege/angebot/:subroute', component: GegeAngebotComponent },
        {
          path: 'thh-hund/angebot/:subroute',
          component: ThhHundAngebotComponent,
        },
        {
          path: 'thh-pferd/angebot/:subroute',
          component: ThhPferdAngebotComponent,
        },
        { path: 'tkv/angebot/:subroute', component: TkvAngebotComponent },
        { path: 'tkv-v2/angebot/:subroute', component: TkvAngebotV2Component },
        { path: 'tgv/angebot/:subroute', component: TgvAngebotComponent },
        { path: 'checkout/:subroute', component: CheckoutComponent },
        { path: 'checkout', component: CheckoutComponent },
      ],
      {
        scrollPositionRestoration: 'enabled',
      },
    ),
    ModalModule,
    AllianzIconModule,
    NxButtonModule,
    NxIsoDateModule,
    NxCheckboxModule,
    FormsModule,
    NxtFieldSetModule,
    NxFormfieldModule,
    OAuthModule.forRoot(),
    NxHeadlineComponent,
    NgOptimizedImage,
    LinkModule,
    NxLayoutComponent,
    NxRowComponent,
    NxColComponent,
    DevMockPanelComponent,
    DashboardComponent,
    VhvAngebotComponent,
    CheckoutComponent,
    PhAngebotComponent,
    RsAngebotComponent,
    RsVermietungAngebotComponent,
    VwgAngebotComponent,
    ThhHundAngebotComponent,
    ThhPferdAngebotComponent,
    TkvAngebotComponent,
    DesignContainerComponent,
    ExpertModeDesignComponent,
    NdbxDesignComponent,
    ErrorpageComponent,
    SessionExpiredPageComponent,
    LogoutpageComponent,
    ConstructionpageComponent,
    UnfAngebotComponent,
    JhpAngebotComponent,
    GegeAngebotComponent,
    VnAddressInvalidComponent,
    TkvAngebotV2Component,
    VwgAngebotV2Component,
    VhvAngebotV2Component,
    TgvAngebotComponent,
    ForwardedToUnderwriterPageComponent,
  ],
  providers: [
    { provide: LOCALE_ID, useValue: 'de' },
    { provide: LocationStrategy, useClass: HashLocationStrategy },
    {
      provide: APP_BASE_HREF,
      useValue: '/',
    },
    {
      provide: TEXT_SERVICE_CONTENT,
      useValue: {
        AO: contentVertreter,
        AZAM: contentAzam,
        SDW: contentSdw,
        DEFAULT: contentDefault,
        default: 'DEFAULT',
      },
    },
    { provide: ErrorHandler, useValue: new GlobalErrorHandler(App.SHOP) },
    {
      provide: 'globalEventDispatcherRef',
      useClass: GlobalEventDispatcherService,
    },
    GlobalActions,
    provideHttpClient(withInterceptorsFromDi()),
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {
  private readonly urlParameterLoader = new UrlParameterLoader(location.search);
  private readonly authCodeFlowConfig: AuthConfig = {
    configId: 'ps20',
    issuer: environment.authorizeUrl,
    redirectUri: environment.appTokenUrl,
    clientId: environment.clientId,
    responseType: 'code',
    scope: environment.scope,
    checkOrigin: false,
    oidc: true,
    logoutUrl: environment.revokeUrl,
    revocationEndpoint: environment.endSessionUrl,
    tokenEndpoint: environment.tokenUrl,
    requestAccessToken: true,
  };

  constructor(
    private readonly ngRedux: NgRedux<IAppState>,
    private readonly oauthService: OAuthService,
    private readonly ngZone: NgZone,
  ) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).ngZone = this.ngZone;
    initDayJs();
    this.ngRedux.provideStore(rootReducer(reducers), localStore.getDelegate());
    this.initShopDependingOnEntryPoint().then(() => {
      // no-op
    });
  }

  async initShopDependingOnEntryPoint(): Promise<void> {
    const partyId: string = null;
    const role: string = null;
    const cryptid: string = null;
    const aid: string = null;
    const pid: string = null;
    const vorid: string = null;
    const copy: string = null;
    const amisupd: string = null;
    const azamVorgangsId: string = null;
    const ngspDeactivated: string = null;
    const testDaten: undefined = null;
    const permasession: boolean = null;
    const accountManager: string = null;
    this.oauthService.configure(this.authCodeFlowConfig);
    this.oauthService.setStorage(sessionStorage);
    this.urlParameterLoader.parseUrlAndPersist();
    const urlParams = this.urlParameterLoader.getParameter();
    initGlobalEventDispatcher();
    if (
      (environment.apigeeActive && urlParams.permasession !== 'true' && !urlParams.code) ||
      ((urlParams.partyId || urlParams.quoteId) && urlParams.role)
    ) {
      await this.sdwAuthorize(urlParams);
    } else if (urlParams.state && urlParams.code) {
      this.sdwRequestTokenAndInit(
        this.urlParameterLoader,
        urlParams,
        partyId,
        role,
        cryptid,
        aid,
        pid,
        vorid,
        copy,
        amisupd,
        azamVorgangsId,
        ngspDeactivated,
        testDaten,
        permasession,
        accountManager,
      );
    } else {
      init(this.urlParameterLoader);
    }
  }

  async sdwAuthorize(urlParams: Record<string, string>): Promise<void> {
    this.oauthService.configure(this.authCodeFlowConfig);
    this.oauthService.setStorage(sessionStorage);
    await this.oauthService.loadDiscoveryDocumentAndLogin();
    this.oauthService.initLoginFlow();
    localStorage.setItem('vtnr', urlParams.vtnr || '');
    const mappedRole = this.mapRole(urlParams.role);
    const isUwUserRole = mappedRole === Role.GER_SDW_UW_USER.toString();
    const resolvedRole = urlParams.partyId || isUwUserRole ? mappedRole : null;
    localStorage.setItem(
      'oAuthState',
      JSON.stringify({
        partyId: urlParams.partyId,
        role: resolvedRole,
        cryptid: urlParams.quoteId || urlParams.cryptid,
        aid: urlParams.aid,
        pid: urlParams.pid,
        vorid: urlParams.vorid,
        copy: urlParams.copy,
        amisupd: urlParams.amisupd,
        azamVorgangsId: urlParams.azamVorgangsId,
        ngspDeactivated: urlParams.ngspDeactivated,
        testDaten: urlParams.TestDaten,
        permasession: urlParams.permasession === 'true',
        accountManager: urlParams.accountManager,
      }),
    );
  }

  mapRole(role: string): string {
    if (role === 'GER-SDW-UW-USER') {
      return Role.GER_SDW_UW_USER.toString();
    } else {
      return role;
    }
  }

  sdwRequestTokenAndInit(
    urlParameterLoader: UrlParameterLoader,
    urlParams: Record<string, string>,
    partyId: string,
    role: string,
    cryptid: string,
    aid: string,
    pid: string,
    vorid: string,
    copy: string,
    amisupd: string,
    azamVorgangsId: string,
    ngspDeactivated: string,
    testDaten: undefined,
    permasession: boolean,
    accountManager: string,
  ): void {
    this.oauthService.scope = '';
    this.oauthService
      .fetchTokenUsingGrant('authorization_code', {
        redirect_uri: environment.appTokenUrl,
        code: urlParams.code,
        code_verifier: sessionStorage.getItem('ps20.PKCE_verifier'),
      })
      .then(response => {
        GlobalEventDispatcher.getInstance().dispatch(
          new GlobalActions().setAccessToken(response.access_token),
        );
      });
    const decodedState = JSON.parse(localStorage.getItem('oAuthState'));
    partyId = decodedState.partyId;
    role = decodedState.role;
    cryptid = decodedState.cryptid;
    aid = decodedState.aid;
    pid = decodedState.pid;
    vorid = decodedState.vorid;
    copy = decodedState.copy;
    amisupd = decodedState.amisupd;
    azamVorgangsId = decodedState.azamVorgangsId;
    ngspDeactivated = decodedState.ngspDeactivated;
    testDaten = decodedState.testDaten;
    permasession = decodedState.permasession;
    accountManager = decodedState.accountManager;
    init(
      urlParameterLoader,
      partyId,
      role,
      cryptid,
      aid,
      pid,
      vorid,
      copy,
      amisupd,
      azamVorgangsId,
      ngspDeactivated,
      testDaten,
      permasession,
      accountManager,
    );
  }
}
